import { empty } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { defineComponent, PropType } from 'vue'

import { Work, Item, isSearchResult, SearchResult } from '@skinsoft/types'
import { expressionSelectors, itemSelectors, manifestationSelectors, workSelectors } from '@skinsoft/selectors'
import { searchResultSelectors } from '@skinsoft/search-data-store'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { Author } from './author'

enum States {
    PENDING = 'pending',
    READY = 'ready',
    ERROR = 'error',
}

export const Description = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sAuthor: Author,
    },
    props: {
        item: {
            type: Object as PropType<Item | SearchResult>,
            required: true,
        },
        itemType: {
            type: String,
        }
    },
    template: require('./description.html'),
    data() {
        return {
            itemIsSearchResult: false,
            state: States.PENDING,
            work: null as any as Work,
            workSelectors,
            searchResultSelectors,
            authorReady: false,
        }
    },
    created() {
        this.itemIsSearchResult = this.itemType == 'SearchResult' ? true : (this.itemType == 'Item' ? false : isSearchResult(this.item))
        this.subscribe()
    },
    methods: {
        onAuthorReady() {
            this.authorReady = true;
            this.processReady();
        },
        formatFromUnit(unit) {
            switch (unit) {
                case 'year':
                    return unit;
                default:
                    return 'short';
            }
        },
        processReady() {
            if (this.authorReady && this.state !== States.PENDING) {
                this.$emit('ready');
            }
        },
        subscribe() {
            if(this.itemIsSearchResult) {
                this.authorReady = true
                this.state = States.READY
            }
            else {
                const manifestationId = itemSelectors.manifestation(this.item);
                if (!manifestationId) {
                    this.authorReady = true; // as we don't have any manifestation available, author won't be rendered at all
                    this.state = States.READY;
                    return;
                }

                this.state = States.PENDING;

                this.subscribeTo(
                    // @ts-ignore
                    this.$manifestationDataStore.fetchOneWithItemType(manifestationId, ((this.item as any).entityType ?? this.item.type)).pipe(
                        switchMap(manifestation => {
                            const expressionId = manifestationSelectors.expression(manifestation);

                            if (!expressionId) {
                                this.authorReady = true; // as we don't have any expression available, author won't be rendered at all
                                this.state = States.READY;
                                return empty();
                            }

                            return this.$expressionDataStore.fetchOne(expressionId);
                        }),
                        switchMap(expression => {
                            const workId = expressionSelectors.work(expression);

                            if (!workId) {
                                this.authorReady = true; // as we don't have any work available, author won't be rendered at all
                                this.state = States.READY;
                                return empty();
                            }

                            return this.$workDataStore.fetchOne(workId);
                        }),
                        catchError(error => {
                            // tslint:disable-next-line:no-console
                            if (error.status === 403) {
                                // trying to fetch manifestation without permission (from another workspace, ...)
                                this.state = States.READY
                                this.authorReady = true
                            } else {
                                console.error(error);
                                this.state = States.ERROR;
                            }

                            return empty();
                        }),
                    ),
                    work => {
                        this.work = work;
                        this.state = States.READY;
                    },
                    'manifestation',
                )
            }
        },
    },
    watch: {
        item() {
            this.unsubscribeFrom('manifestation');
            if(this.item) {
                this.itemType == 'SearchResult' ? true : (this.itemType == 'Item' ? false : isSearchResult(this.item))
                this.subscribe();
            }
        },
        state() {
            this.processReady();
        },
    },
})
